import React, { useState } from "react";
import cropr, { BackboneModel, Model, Parcel, ParcelModel } from "../../shared/utils/cropr";
import { gettext } from "../../shared/utils/legacy";
import { Modal, Tab } from "react-bootstrap";
import GeometryModelPreview from "../../shared/components/GeometryModelPreview";
import LabeledField from "../../shared/forms/LabeledField";
import LabeledSelect from "../../shared/forms/LabeledSelect";
import ModalHeader from "../../shared/components/ModalHeader";
import useCroprBackbone from "../../shared/hooks/useCroprBackbone";
import { getSoilOptions, getSoilTriangles } from "../../shared/utils/soil";
import GeneralError from "../../shared/forms/GeneralError";
import SoilTriangleCroprCodes from "../../shared/components/SoilTriangleCroprCodes";
import ModelEditModalFooter from "../../shared/components/ModelEditModalFooter";
import { ModelSubmitFormik, PropertiesModalProps } from "../../shared/utils/modals";
import { useFormikContext } from "formik";
import { useReferenceData } from "../../shared/hooks/useReferenceData";
import FileOverview from "../../fileupload/FileOverview";
import { convertAttachmentIds, getAttachmentIds } from "../../fileupload/utils";

const ParcelPropertiesModal = ({ model, onSave, onHide }: PropertiesModalProps<ParcelModel>) => {
  const [show, setShow] = useState(true);
  const typeName = gettext("parcel");
  const referenceData = useReferenceData(model).data;

  function hide() {
    const coWorkerView: any = cropr.NewCoWorkerView;
    if (coWorkerView?.success) coWorkerView.closePopOver();
    setShow(false);
    onHide?.();
  }
  const attachments = convertAttachmentIds(model.get("attachments"));
  const initialValues: Parcel = {
    name: model.get("name") || Model.Parcel.generateName(),
    registered_area: model.get("new_area") || model.get("registered_area") || "",
    owner: model.get("owner"),
    soil_type: model.get("soil_type") || referenceData?.soil_type,
    regulatory_soil_type: model.get("regulatory_soil_type") || referenceData?.regulatory_soil_type,
    attachments: model.get("attachments"),
    geometry: model.get("geometry") || model.get("addshape_geometry"),
  };
  const props = {
    hide,
    typeName,
    model,
    enableReinitialize: true,
    collection: cropr.parcels,
    permission: cropr.permissions.parcel,
  };
  return (
    <Modal
      size="xl"
      className="modal-fh dfi-modal"
      show={show}
      onHide={hide}
      backdrop="static"
      enforceFocus={false}
    >
      <ModelSubmitFormik initialValues={initialValues} onSave={onSave} {...props}>
        {({ handleSubmit, isSubmitting, setFieldValue, errors }) => (
          <form onSubmit={handleSubmit}>
            <Tab.Container defaultActiveKey="parcel">
              <ModalHeader
                tabs={[
                  ["parcel", gettext("Parcel")],
                  ["attachments", gettext("Attachments")],
                ]}
              />
              <Modal.Body className="py-3">
                <GeneralError errors={errors} />
                <Tab.Content className="py-3">
                  <ParcelPane model={model} />
                  <Tab.Pane eventKey="attachments">
                    <div id="parcel_attachments" className="tab-pane column-container">
                      <div className="span12">
                        <FileOverview
                          attachments={attachments}
                          permission={cropr.permissions.parcel}
                          onChange={(attachmentKeys: string[]) =>
                            setFieldValue("attachments", getAttachmentIds(attachmentKeys))
                          }
                        />
                      </div>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Modal.Body>
            </Tab.Container>
            <ModelEditModalFooter disabled={isSubmitting} {...props} />
          </form>
        )}
      </ModelSubmitFormik>
    </Modal>
  );
};

const ParcelPane = ({ model }: { model: ParcelModel }) => {
  const { values, setFieldValue } = useFormikContext<Parcel>();
  const [contacts] = useCroprBackbone("farm_contacts");
  const coWorkerView: any = cropr.NewCoWorkerView;
  const contactOptions = contacts
    .filter((fc: any) => (fc.is_owner && !fc.deleted) || model.get("owner") === fc.id)
    .map((fc: any) => ({
      value: fc.id,
      label: fc.name,
    }));
  return (
    <Tab.Pane eventKey="parcel">
      <div className="row">
        <div className="col-6 b-r p-4">
          <GeometryModelPreview model={model} />
          <LabeledField
            label={gettext("Name")}
            name="name"
            type="text"
            placeholder={gettext("Enter a parcelname")}
          />
          <LabeledField
            label={`${gettext("Area")} ${cropr.l10n.unit_area_display}`}
            name="registered_area"
            placeholder={gettext("Area")}
            type="text"
          />
          <LabeledSelect
            label={gettext("Owner")}
            id="parcel_modal_owner"
            name="owner"
            type={cropr.features.customer_module ? "text" : "hidden"}
            options={contactOptions}
            onCreateOption={
              coWorkerView
                ? (inputValue: any) => {
                    coWorkerView.render(
                      {
                        currentTarget: "#parcel_modal_owner",
                      },
                      inputValue,
                      "owner",
                      undefined,
                      false,
                      (model?: BackboneModel) => {
                        if (model) {
                          setFieldValue("owner", model.id);
                        }
                      }
                    );
                  }
                : undefined
            }
            placeholder={gettext("Choose an owner")}
          />
          <LabeledSelect
            label={gettext("Soil type")}
            menuPlacement="auto"
            options={getSoilOptions({
              country: model.get("country"),
            })}
            name="soil_type"
            placeholder={gettext("Choose a soiltype")}
          />
          {model.get("country") === "NL" && (
            <LabeledSelect
              label={gettext("Regulatory soil type")}
              menuPlacement="auto"
              options={getSoilOptions({
                country: model.get("country"),
                base: cropr.reg_soiltype,
              })}
              name="regulatory_soil_type"
              placeholder={gettext("Choose a regulatory soiltype")}
            />
          )}
        </div>

        <div className="col-6 p-4 text-center">
          {getSoilTriangles({
            country: model.get("country"),
          }).map((triangle) => (
            <SoilTriangleCroprCodes
              key={triangle}
              size={250}
              className="pb-3"
              selected={values["soil_type"]}
              onChange={(val) => {
                setFieldValue("soil_type", val);
              }}
              textBreak={false}
              triangle={triangle}
            />
          ))}
        </div>
      </div>
    </Tab.Pane>
  );
};

export default ParcelPropertiesModal;
